'use server';

import { publicError } from '@/lib/action-errors';
import { query, transaction } from '@/lib/db';
import { revalidatePath } from 'next/cache';
import { getSession, setSessionCookie, clearSessionCookie, UserSession } from '@/lib/session';
import { protectAdminAccount } from '@/lib/admin-protect';
import { createAuditLog } from '@/lib/audit';
import { auditActionMatches } from '@/lib/audit-actions';
import { isUuid } from '@/lib/listing-policy';
import { tryGetSecret } from '@/lib/secrets';
import { sendAgencyApprovedEmail, sendAgencyRejectedEmail } from '@/lib/agency-emails';
import { getSiteUrl } from '@/lib/site-url';
import { agencyPath } from '@/lib/agency-slug';
import { removeUnreferencedUploads } from '@/lib/uploads-cleanup';
import { countsAsListingSql } from '@/lib/listing-policy';
import { findWaitingBuyers, tellBuyersListingWithdrawn } from '@/lib/listing-withdrawn';

export async function getAdminProfile() {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    const res = await query(
      `SELECT key, val_en, val_fr FROM site_settings WHERE key IN ('admin_name', 'admin_email', 'admin_avatar')`
    );
    const map: Record<string, string> = {};
    res.rows.forEach((r: any) => {
      map[r.key] = r.val_en || '';
    });

    return {
      success: true,
      profile: {
        name: map['admin_name'] || 'System Admin',
        email: map['admin_email'] || 'admin@realestatechannel.ca',
        avatar: map['admin_avatar'] || '/uploads/agency-apex-logo.webp',
      }
    };
  } catch (e: any) {
    console.error('Error fetching admin profile:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function updateAdminProfile(data: { name: string; email: string; avatar: string }) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    const items = [
      { key: 'admin_name', val: data.name },
      { key: 'admin_email', val: data.email },
      { key: 'admin_avatar', val: data.avatar },
    ];

    for (const item of items) {
      await query(`
        INSERT INTO site_settings (key, val_en, val_fr)
        VALUES ($1, $2, $2)
        ON CONFLICT (key) DO UPDATE SET val_en = EXCLUDED.val_en, val_fr = EXCLUDED.val_fr
      `, [item.key, item.val]);
    }

    revalidatePath('/admin/profile', 'page');
    await createAuditLog('update_admin_profile', 'system', '', { fields: items.map((i) => i.key) });
    return { success: true };
  } catch (e: any) {
    console.error('Error updating admin profile:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function getAdminUsers(params?: { search?: string; typeFilter?: string; page?: number; pageSize?: number }) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required', users: [], total: 0, page: 1, totalPages: 1 };
    }

    const search = params?.search?.trim() || '';
    const typeFilter = params?.typeFilter || 'all';
    const page = Math.max(1, params?.page || 1);
    const pageSize = params?.pageSize ? Math.max(1, Math.min(100, params.pageSize)) : 25;
    const offset = (page - 1) * pageSize;

    const conditions: string[] = [];
    const values: any[] = [];

    if (search) {
      values.push(`%${search}%`);
      conditions.push(`(u.email ILIKE $${values.length} OR u.name ILIKE $${values.length} OR u.phone ILIKE $${values.length} OR u.city ILIKE $${values.length})`);
    }

    if (typeFilter === 'agencies') {
      conditions.push(`u.is_realtor = TRUE`);
    } else if (typeFilter === 'pending_agencies') {
      conditions.push(`u.is_realtor = TRUE AND u.agency_status = 'pending'`);
    } else if (typeFilter === 'sellers') {
      conditions.push(`(u.is_realtor = FALSE OR u.is_realtor IS NULL) AND u.role <> 'admin' AND NOT EXISTS (SELECT 1 FROM agency_members m WHERE m.user_id = u.id)`);
    } else if (typeFilter === 'blocked') {
      conditions.push(`u.is_blocked = TRUE`);
    }

    const whereClause = conditions.length > 0 ? ` WHERE ` + conditions.join(' AND ') : '';

    // Total count query
    const countRes = await query(`SELECT COUNT(DISTINCT u.id)::int as total FROM users u ${whereClause}`, values);
    const total = countRes.rows[0]?.total || 0;

    const sql = `
      SELECT 
        u.id, 
        u.email, 
        u.name, 
        u.phone, 
        u.city, 
        u.province, 
        u.role, 
        COALESCE(u.is_realtor, FALSE) as is_realtor,
        u.agency_status,
        COALESCE(u.is_blocked, FALSE) as is_blocked,
        COALESCE(u.auto_approve_listings, FALSE) as auto_approve_listings,
        u.block_reason,
        u.created_at,
        COUNT(p.id)::int as total_properties,
        COUNT(p.id) FILTER (WHERE p.status = 'active')::int as active_properties,
        COUNT(p.id) FILTER (WHERE p.status = 'pending')::int as pending_properties,
        COUNT(p.id) FILTER (WHERE p.status = 'rejected')::int as rejected_properties,
        -- A manager or consultant is not a private seller: the agency they
        -- work for and their role there (admin check, 2026-09-26).
        (SELECT json_build_object('agency_name', a.name, 'agency_email', a.email, 'role', m.role)
           FROM agency_members m JOIN users a ON a.id = m.agency_id
          WHERE m.user_id = u.id LIMIT 1) as agency_membership
      FROM users u
      LEFT JOIN properties p ON (p.user_id = u.id OR LOWER(p.contact_email) = LOWER(u.email))
      ${whereClause}
      GROUP BY u.id 
      ORDER BY u.created_at DESC, u.id
      LIMIT $${values.length + 1} OFFSET $${values.length + 2}
    `;

    const dataValues = [...values, pageSize, offset];
    const res = await query(sql, dataValues);

    return { 
      success: true, 
      users: res.rows, 
      total, 
      page, 
      pageSize, 
      totalPages: Math.ceil(total / pageSize) || 1 
    };
  } catch (e: any) {
    console.error('Error fetching admin users:', e);
    return { success: false, error: publicError(e), users: [], total: 0, page: 1, totalPages: 1 };
  }
}

export async function toggleUserAgencyStatus(userId: string, isRealtor: boolean) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    // Changing the role of an administrator account is a demotion, whichever way the
    // box is ticked.
    const refusal = await protectAdminAccount(session, userId, 'demote');
    if (refusal) return { success: false, error: refusal };

    // Promoting an account is not the same decision as publishing its showcase.
    // A personal account carries a stored 'approved' from registration, so without
    // this the checkbox alone put a live, unreviewed agency on the site - and left
    // no audit entry, because only toggleUserAgencyStatus writes one. The showcase
    // now waits for that explicit approval, which also emails the owner.
    // The CASE reads the row as it was before this UPDATE, so an account that was
    // already an agency keeps whatever status it had.
    await query(`
      UPDATE users 
      SET is_realtor = $1,
          role = CASE WHEN $1 = TRUE THEN 'realtor' ELSE 'user' END,
          agency_status = CASE
            WHEN $1 = TRUE AND is_realtor IS NOT TRUE THEN 'pending'
            ELSE agency_status
          END
      WHERE id = $2
    `, [isRealtor, userId]);

    await createAuditLog(isRealtor ? 'promote_to_agency' : 'demote_from_agency', 'user', userId, {});
    return { success: true };
  } catch (e: any) {
    console.error('Error toggling user agency status:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function toggleUserAutoApprove(userId: string, autoApprove: boolean) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    await query(`
      UPDATE users 
      SET auto_approve_listings = $1
      WHERE id = $2
    `, [autoApprove, userId]);

    await createAuditLog(autoApprove ? 'grant_auto_approve' : 'revoke_auto_approve', 'user', userId, {});
    return { success: true };
  } catch (e: any) {
    console.error('Error toggling user auto-approve status:', e);
    return { success: false, error: publicError(e) };
  }
}

/** The account that owns a listing: the one "trust" and "ban" are about. The contact address in the text is written by the seller and names nobody. */
async function listingOwnerId(propertyId: string): Promise<string | null> {
  if (!isUuid(propertyId)) return null;
  const r = await query(`SELECT user_id FROM properties WHERE id = $1`, [propertyId]);
  return r.rows[0]?.user_id ?? null;
}

export async function setListingAuthorTrusted(propertyId: string, trusted: boolean) {
  const session = await getSession();
  if (!session || session.role !== 'admin') return { success: false, error: 'Unauthorized: Admin session required' };
  const ownerId = await listingOwnerId(propertyId);
  if (!ownerId) return { success: false, error: 'no_account' };
  try {
    await query(`UPDATE users SET auto_approve_listings = $1 WHERE id = $2`, [trusted, ownerId]);
    await createAuditLog(trusted ? 'grant_auto_approve' : 'revoke_auto_approve', 'user', ownerId, { fromListing: propertyId });
    return { success: true };
  } catch (e: any) {
    console.error('Error setting trusted author:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function banListingAuthor(propertyId: string) {
  const session = await getSession();
  if (!session || session.role !== 'admin') return { success: false, error: 'Unauthorized: Admin session required' };
  const ownerId = await listingOwnerId(propertyId);
  if (!ownerId) return { success: false, error: 'no_account' };
  return toggleUserBlockStatus(ownerId, true, 'Banned by moderator');
}

export async function toggleUserBlockStatus(userId: string, isBlocked: boolean, reason?: string) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    if (isBlocked) {
      const refusal = await protectAdminAccount(session, userId, 'block');
      if (refusal) return { success: false, error: refusal };
    }

    await query(`
      UPDATE users 
      SET is_blocked = $1,
          block_reason = $2
      WHERE id = $3
    `, [isBlocked, isBlocked ? (reason || 'Blocked by administrator') : null, userId]);

    // The contact email on a listing is free text, so it names an owner only for a
    // listing nobody has claimed; it must never reach into another person's listings.
    // If blocked, set is_hidden_by_admin to true without altering the original property status
    if (isBlocked) {
      await query(`
        UPDATE properties 
        SET is_hidden_by_admin = TRUE, updated_at = NOW() 
        WHERE (user_id = $1 OR (user_id IS NULL AND LOWER(contact_email) IN (SELECT LOWER(email) FROM users WHERE id = $1)))
      `, [userId]);
    } else {
      // If unblocked, clear the admin hidden flag; property status remains intact
      await query(`
        UPDATE properties 
        SET is_hidden_by_admin = FALSE, updated_at = NOW() 
        WHERE (user_id = $1 OR (user_id IS NULL AND LOWER(contact_email) IN (SELECT LOWER(email) FROM users WHERE id = $1)))
      `, [userId]);
    }

    await createAuditLog(isBlocked ? 'block_user' : 'unblock_user', 'user', userId, { reason: reason || null });
    return { success: true };
  } catch (e: any) {
    console.error('Error toggling user block status:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function adminUpdateUser(
  userId: string,
  data: { name?: string; phone?: string; email?: string }
) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    const currentRes = await query(`SELECT email FROM users WHERE id = $1`, [userId]);
    if (currentRes.rows.length === 0) {
      return { success: false, error: 'User not found' };
    }
    const oldEmail = currentRes.rows[0].email;
    const newEmail = data.email?.trim().toLowerCase() || oldEmail;

    // Check duplicate email if changed
    if (newEmail !== oldEmail) {
      const dupCheck = await query(`SELECT id FROM users WHERE LOWER(email) = LOWER($1) AND id != $2`, [newEmail, userId]);
      if (dupCheck.rows.length > 0) {
        return { success: false, error: 'Email is already in use by another user' };
      }
    }

    await query(`
      UPDATE users
      SET name = $1,
          phone = $2,
          email = $3
      WHERE id = $4
    `, [data.name?.trim() || null, data.phone?.trim() || null, newEmail, userId]);

    // Update properties contact_email if user email changed
    if (newEmail !== oldEmail) {
      await query(`
        UPDATE properties
        SET contact_email = $1
        WHERE user_id = $2 OR (user_id IS NULL AND LOWER(contact_email) = LOWER($3))
      `, [newEmail, userId, oldEmail]);
    }

    await createAuditLog('update_user_profile', 'user', userId, { fields: Object.keys(data || {}) });
    return { success: true };
  } catch (e: any) {
    console.error('Error updating user as admin:', e);
    return { success: false, error: publicError(e) };
  }
}

/** Closes the reports about a listing without touching the listing: they were looked at and there was nothing to do. */
export async function dismissListingReports(propertyId: string) {
  const session = await getSession();
  if (!session || session.role !== 'admin') return { success: false, error: 'Unauthorized: Admin session required' };
  if (!isUuid(propertyId)) return { success: false, error: 'missing' };
  try {
    const res = await query(`DELETE FROM complaints WHERE property_id = $1 RETURNING id`, [propertyId]);
    await createAuditLog('dismiss_reports', 'property', propertyId, { reports: res.rows.length });
    return { success: true, closed: res.rows.length };
  } catch (e: any) {
    console.error('Error dismissing reports:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function getAdminComplaints(limit = 50, offset = 0) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized', complaints: [], total: 0 };
    }

    const countRes = await query(`SELECT COUNT(*)::int as total FROM complaints`);
    const total = countRes.rows[0]?.total || 0;

    const res = await query(`
      SELECT c.*, p.title_en, p.title_fr, p.status as property_status, p.slug as property_slug,
             p.province AS property_province, p.city AS property_city, p.listing_type AS property_listing_type,
             (SELECT count(*)::int FROM complaints c2 WHERE c2.property_id = c.property_id) AS property_report_count
      FROM complaints c
      LEFT JOIN properties p ON c.property_id = p.id
      ORDER BY c.created_at DESC, c.id
      LIMIT $1 OFFSET $2
    `, [limit, offset]);

    return {
      success: true,
      complaints: res.rows,
      total
    };
  } catch (error: any) {
    console.error('Error fetching admin complaints:', error);
    return { success: false, error: publicError(error), complaints: [], total: 0 };
  }
}

export async function adminDeleteUser(userId: string, softDelete = true) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    const refusal = await protectAdminAccount(session, userId, 'delete');
    if (refusal) return { success: false, error: refusal };

    const userRes = await query(`SELECT email FROM users WHERE id = $1`, [userId]);
    const email = userRes.rows[0]?.email;

    // Which listings belong to this user: their own, plus any nobody has
    // claimed that were published under their address. The contact email is
    // free text - whoever writes a listing chooses it - so it must never pull
    // in a listing that already has an owner. It used to, and deleting one
    // person removed other people's listings that merely named them.
    // The ::text casts fix a separate failure: with the parameter untyped,
    // Postgres cannot choose between lower(text) and lower(anyrange) and
    // refuses the statement (42P08), so this action could not run at all.
    if (softDelete) {
      await query(`UPDATE users SET deleted_at = NOW() WHERE id = $1`, [userId]);
      await query(`
        UPDATE properties 
        SET deleted_at = NOW(), status = 'archived', updated_at = NOW() 
        WHERE (user_id = $1 OR (user_id IS NULL AND $2::text IS NOT NULL AND LOWER(contact_email) = LOWER($2::text)))
          AND deleted_at IS NULL
      `, [userId, email]);

      await createAuditLog('soft_delete_user', 'user', userId, { email, admin: session.email });
      return { success: true };
    }

    // Permanent delete. Note the uploads before the rows go: afterwards there
    // is nothing left to say which files were theirs.
    const ownedUploads = await query<{ url: string | null }>(
      `SELECT ph.url FROM photos ph JOIN properties p ON p.id = ph.property_id
         WHERE p.user_id = $1 OR (p.user_id IS NULL AND $2::text IS NOT NULL AND LOWER(p.contact_email) = LOWER($2::text))
       UNION ALL SELECT unnest(p.photos) FROM properties p
         WHERE p.user_id = $1 OR (p.user_id IS NULL AND $2::text IS NOT NULL AND LOWER(p.contact_email) = LOWER($2::text))
       UNION ALL SELECT m.url FROM media m WHERE m.owner_id = $1
       UNION ALL SELECT u.avatar_url FROM users u WHERE u.id = $1
       UNION ALL SELECT u.banner_url FROM users u WHERE u.id = $1
       UNION ALL SELECT u.logo_url FROM users u WHERE u.id = $1`,
      [userId, email]
    );

    // A-16, the administrator's way in: the same buyers are told, and the
    // rows go all at once or not at all.
    const waiting = await findWaitingBuyers(userId, email);

    await transaction(async (exec) => {
      await exec(`
        DELETE FROM complaints 
        WHERE property_id IN (
          SELECT id FROM properties 
          WHERE user_id = $1 OR (user_id IS NULL AND $2::text IS NOT NULL AND LOWER(contact_email) = LOWER($2::text))
        )
      `, [userId, email]);

      await exec(`
        DELETE FROM photos 
        WHERE property_id IN (
          SELECT id FROM properties 
          WHERE user_id = $1 OR (user_id IS NULL AND $2::text IS NOT NULL AND LOWER(contact_email) = LOWER($2::text))
        )
      `, [userId, email]);

      await exec(`
        DELETE FROM properties 
        WHERE user_id = $1 OR (user_id IS NULL AND $2::text IS NOT NULL AND LOWER(contact_email) = LOWER($2::text))
      `, [userId, email]);

      await exec(`DELETE FROM users WHERE id = $1`, [userId]);

      // A search saved by address alone is not reached by the cascade from users.
      if (email) {
        await exec(`DELETE FROM saved_searches WHERE LOWER(email) = LOWER($1::text)`, [email]);
      }
    });

    await tellBuyersListingWithdrawn(waiting);
    await removeUnreferencedUploads(ownedUploads.rows.map((r) => r.url));

    await createAuditLog('hard_delete_user', 'user', userId, { email, admin: session.email });

    return { success: true };
  } catch (e: any) {
    console.error('Error deleting user as admin:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function adminImpersonateUser(targetUserId: string) {
  try {
    const currentSession = await getSession();
    // Only an administrator's own session may begin this. A session that is already
    // standing in for somebody would let the chain grow with nobody named at its
    // start, and it cannot be told apart from the person it stands for.
    if (!currentSession || currentSession.role !== 'admin' || currentSession.impersonatorAdminEmail) {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    const adminEmail = currentSession.email;

    if (!isUuid(targetUserId)) {
      return { success: false, error: 'Target user not found' };
    }
    const userRes = await query(
      `SELECT id, email, name, role, is_realtor, is_blocked, deleted_at, session_version FROM users WHERE id = $1`,
      [targetUserId]
    );

    if (userRes.rowCount === 0) {
      return { success: false, error: 'Target user not found' };
    }

    const targetUser = userRes.rows[0];

    // Never into an administrator (the way back would then be an ordinary sign-in),
    // and never into an account that cannot sign in for itself.
    if (targetUser.role === 'admin') {
      return { success: false, error: 'Administrator accounts cannot be impersonated.' };
    }
    if (targetUser.is_blocked || targetUser.deleted_at) {
      return { success: false, error: 'This account is blocked or deleted.' };
    }

    await createAuditLog('impersonate_start', 'user', targetUser.id, { target_email: targetUser.email }, adminEmail);

    // The session keeps who is behind it, so the banner's "stop" can bring them back.
    await setSessionCookie({
      userId: targetUser.id,
      email: targetUser.email,
      name: targetUser.name,
      role: targetUser.is_realtor ? 'realtor' : 'user',
      realtorId: targetUser.is_realtor ? targetUser.id : undefined,
      impersonatorAdminEmail: adminEmail,
      sv: Number(targetUser.session_version || 0),
    }, 7);

    return {
      success: true,
      redirectUrl: targetUser.is_realtor ? '/en/cabinet/realtor/settings' : '/en/my-listings'
    };
  } catch (e: any) {
    console.error('Error impersonating user:', e);
    return { success: false, error: publicError(e) };
  }
}

// The environment administrator has no users row; it is the way back only while it
// is still configured and no real account holds the address (as at sign-in).
const ENVIRONMENT_ADMIN_EMAIL = 'admin@realestatechannel.ca';

export async function adminStopImpersonation() {
  try {
    const currentSession = await getSession();
    if (!currentSession || !currentSession.impersonatorAdminEmail) {
      return { success: false, error: 'Not currently impersonating' };
    }

    const adminEmail = currentSession.impersonatorAdminEmail;
    const adminRes = await query(
      `SELECT id, email, name, role, is_blocked, deleted_at, session_version FROM users WHERE LOWER(email) = LOWER($1)`,
      [adminEmail]
    );
    const adminUser = adminRes.rows[0];

    // An administrator session is handed back only to an administrator who is still
    // one. If the account was demoted, blocked or removed while it was standing in
    // for somebody, the way back is closed and the browser signed out.
    let restored = false;
    if (adminUser) {
      if (adminUser.role === 'admin' && !adminUser.is_blocked && !adminUser.deleted_at) {
        await setSessionCookie({
          userId: adminUser.id,
          email: adminUser.email,
          name: adminUser.name || 'Admin',
          role: 'admin',
          sv: Number(adminUser.session_version || 0),
        }, 7);
        restored = true;
      }
    } else if (adminEmail.toLowerCase() === ENVIRONMENT_ADMIN_EMAIL && tryGetSecret('ADMIN_PASSWORD')) {
      await setSessionCookie({ email: ENVIRONMENT_ADMIN_EMAIL, role: 'admin', name: 'System Administrator' }, 7);
      restored = true;
    }

    if (!restored) {
      await clearSessionCookie();
      return { success: false, error: 'The administrator account is no longer active. Sign in again.' };
    }

    await createAuditLog('impersonate_stop', 'user', currentSession.userId || '', {}, adminEmail);
    return { success: true, redirectUrl: '/admin/moderation' };
  } catch (e: any) {
    console.error('Error stopping impersonation:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function getAuditLogs(params?: {
  limit?: number;
  offset?: number;
  entityType?: string;
  action?: string;
  search?: string;
}) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required', logs: [], total: 0 };
    }

    const limit = Math.min(100, Math.max(10, params?.limit || 50));
    const offset = Math.max(0, params?.offset || 0);

    const conditions: string[] = [];
    const values: any[] = [];

    if (params?.entityType && params.entityType !== 'all') {
      values.push(params.entityType);
      conditions.push(`entity_type = $${values.length}`);
    }

    if (params?.action && params.action !== 'all') {
      values.push(auditActionMatches(params.action));
      conditions.push(`action = ANY($${values.length}::text[])`);
    }

    if (params?.search && params.search.trim()) {
      values.push(`%${params.search.trim()}%`);
      conditions.push(`(actor_email ILIKE $${values.length} OR entity_id ILIKE $${values.length} OR details::text ILIKE $${values.length})`);
    }

    const where = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '';

    const countRes = await query(`SELECT COUNT(*)::int as total FROM audit_logs ${where}`, values);
    const total = countRes.rows[0]?.total || 0;

    const dataValues = [...values, limit, offset];
    const res = await query(`
      SELECT id, entity_type, entity_id, action, actor_email, details, created_at
      FROM audit_logs
      ${where}
      ORDER BY created_at DESC, id
      LIMIT $${values.length + 1} OFFSET $${values.length + 2}
    `, dataValues);

    return { success: true, logs: res.rows, total };
  } catch (e: any) {
    console.error('Error fetching audit logs:', e);
    return { success: false, error: publicError(e), logs: [], total: 0 };
  }
}

export async function getTrashItems() {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required', properties: [], users: [] };
    }

    const propRes = await query(`
      SELECT p.id, p.title_en, p.title_fr, p.city, p.province, p.price, p.status, p.deleted_at,
             p.contact_email, p.property_type,
             COALESCE((SELECT json_agg(ph.url ORDER BY ph.display_order, ph.id) FROM photos ph WHERE ph.property_id = p.id), '[]'::json) as photos
      FROM properties p
      WHERE p.deleted_at IS NOT NULL
      ORDER BY p.deleted_at DESC
      LIMIT 100
    `);

    const userRes = await query(`
      SELECT u.id, u.email, u.name, u.role, u.is_realtor, u.deleted_at, u.created_at,
             (SELECT COUNT(*)::int FROM properties WHERE user_id = u.id) as properties_count
      FROM users u
      WHERE u.deleted_at IS NOT NULL
      ORDER BY u.deleted_at DESC
      LIMIT 100
    `);

    return {
      success: true,
      properties: propRes.rows,
      users: userRes.rows
    };
  } catch (e: any) {
    console.error('Error fetching trash items:', e);
    return { success: false, error: publicError(e), properties: [], users: [] };
  }
}

export async function restorePropertyFromTrash(propertyId: string) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    await query(`
      UPDATE properties
      SET deleted_at = NULL, status = 'paused', updated_at = NOW()
      WHERE id = $1
    `, [propertyId]);

    await createAuditLog('restore_property', 'property', propertyId, { restored_to_status: 'paused', admin: session.email });
    return { success: true };
  } catch (e: any) {
    console.error('Error restoring property from trash:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function purgePropertyPermanently(propertyId: string) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    const goneUrls = await query<{ url: string | null }>(
      `SELECT url FROM photos WHERE property_id = $1
       UNION ALL SELECT unnest(photos) FROM properties WHERE id = $1`,
      [propertyId]
    );
    await query(`DELETE FROM complaints WHERE property_id = $1`, [propertyId]);
    await query(`DELETE FROM photos WHERE property_id = $1`, [propertyId]);
    await query(`DELETE FROM properties WHERE id = $1`, [propertyId]);
    await removeUnreferencedUploads(goneUrls.rows.map((r) => r.url));

    await createAuditLog('purge_property_permanently', 'property', propertyId, { admin: session.email });
    return { success: true };
  } catch (e: any) {
    console.error('Error permanently deleting property:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function restoreUserFromTrash(userId: string) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    // Only what the deletion of the account took away comes back: a listing the owner
    // had deleted on its own, earlier, stays deleted.
    const gone = await query(`SELECT deleted_at FROM users WHERE id = $1`, [userId]);
    const deletedAt = gone.rows[0]?.deleted_at ?? null;

    await query(`UPDATE users SET deleted_at = NULL WHERE id = $1`, [userId]);
    if (deletedAt) {
      await query(
        `UPDATE properties SET deleted_at = NULL, status = 'paused' WHERE user_id = $1 AND deleted_at >= $2`,
        [userId, deletedAt]
      );
    }

    await createAuditLog('restore_user', 'user', userId, { admin: session.email });
    return { success: true };
  } catch (e: any) {
    console.error('Error restoring user from trash:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function purgeUserPermanently(userId: string) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    // Call hard delete
    return await adminDeleteUser(userId, false);
  } catch (e: any) {
    console.error('Error permanently purging user:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function getAdminAnalytics(days = 30) {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    // Clamp to a sane range: the value reaches SQL as a bound parameter, and a
    // non-numeric or out-of-range input must never widen the scan window.
    const rangeDays = Math.min(Math.max(Math.trunc(Number(days)) || 30, 1), 365);

    // 1. Daily registrations for the last X days
    const regRes = await query(`
      WITH dates AS (
        SELECT generate_series(
          CURRENT_DATE - (($1::int - 1) * INTERVAL '1 day'),
          CURRENT_DATE,
          '1 day'::interval
        )::date AS day
      )
      SELECT
        TO_CHAR(d.day, 'YYYY-MM-DD') as date,
        COUNT(u.id)::int as users,
        COUNT(u.id) FILTER (WHERE u.is_realtor = true)::int as agencies
      FROM dates d
      LEFT JOIN users u ON DATE(u.created_at) = d.day AND u.deleted_at IS NULL
      GROUP BY d.day
      ORDER BY d.day ASC
    `, [rangeDays]);

    // 2. Daily property publications for the last X days
    const propRes = await query(`
      WITH dates AS (
        SELECT generate_series(
          CURRENT_DATE - (($1::int - 1) * INTERVAL '1 day'),
          CURRENT_DATE,
          '1 day'::interval
        )::date AS day
      )
      SELECT
        TO_CHAR(d.day, 'YYYY-MM-DD') as date,
        COUNT(p.id)::int as total_submitted,
        COUNT(p.id) FILTER (WHERE p.status = 'active')::int as active_now
      FROM dates d
      LEFT JOIN properties p ON DATE(p.created_at) = d.day AND p.deleted_at IS NULL AND ${countsAsListingSql('p')}
      GROUP BY d.day
      ORDER BY d.day ASC
    `, [rangeDays]);

    // Merge time series
    const dailyMap: Record<string, any> = {};
    regRes.rows.forEach((r: any) => {
      dailyMap[r.date] = { date: r.date, users: r.users, agencies: r.agencies, listings: 0, active: 0 };
    });
    propRes.rows.forEach((r: any) => {
      if (dailyMap[r.date]) {
        dailyMap[r.date].listings = r.total_submitted;
        dailyMap[r.date].active = r.active_now;
      } else {
        dailyMap[r.date] = { date: r.date, users: 0, agencies: 0, listings: r.total_submitted, active: r.active_now };
      }
    });

    const timeSeries = Object.values(dailyMap);

    // 3. Status distribution
    const statusRes = await query(`
      SELECT 
        COALESCE(status, 'unknown') as status, 
        COUNT(*)::int as count 
      FROM properties p
      WHERE deleted_at IS NULL AND ${countsAsListingSql('p')}
      GROUP BY status
    `);

    // 4. Property type distribution
    const typeRes = await query(`
      SELECT 
        COALESCE(NULLIF(property_type, ''), 'Other') as type, 
        COUNT(*)::int as count 
      FROM properties p
      WHERE deleted_at IS NULL AND ${countsAsListingSql('p')}
      GROUP BY property_type
      ORDER BY count DESC
      LIMIT 8
    `);

    // 5. Overall summary counts
    const summaryRes = await query(`
      SELECT 
        (SELECT COUNT(*)::int FROM users WHERE deleted_at IS NULL) as total_users,
        (SELECT COUNT(*)::int FROM users WHERE is_realtor = true AND deleted_at IS NULL) as total_agencies,
        (SELECT COUNT(*)::int FROM properties p WHERE deleted_at IS NULL AND ${countsAsListingSql('p')}) as total_properties,
        (SELECT COUNT(*)::int FROM properties WHERE status = 'active' AND deleted_at IS NULL) as active_properties,
        (SELECT COUNT(*)::int FROM properties WHERE status = 'pending' AND deleted_at IS NULL) as pending_moderation,
        (SELECT COUNT(*)::int FROM properties p WHERE deleted_at IS NOT NULL AND ${countsAsListingSql('p')}) as trashed_properties,
        (SELECT COALESCE(SUM(views_count), 0)::int FROM properties WHERE deleted_at IS NULL) as total_views,
        (SELECT COALESCE(SUM(contacts_revealed_count), 0)::int FROM properties WHERE deleted_at IS NULL) as total_contact_reveals,
        (SELECT COUNT(*)::int FROM complaints) as total_complaints,
        (SELECT COUNT(*)::int FROM audit_logs) as total_audit_actions
    `);

    return {
      success: true,
      timeSeries,
      statusDistribution: statusRes.rows,
      typeDistribution: typeRes.rows,
      summary: summaryRes.rows[0] || {}
    };
  } catch (e: any) {
    console.error('Error fetching admin analytics:', e);
    return { success: false, error: publicError(e) };
  }
}

export async function updateAgencyStatusAction(userId: string, status: 'pending' | 'approved' | 'rejected') {
  try {
    const session = await getSession();
    if (!session || session.role !== 'admin') {
      return { success: false, error: 'Unauthorized: Admin session required' };
    }

    const updated = await query(`
      UPDATE users 
      SET agency_status = $1 
      WHERE id = $2 AND is_realtor = true
      RETURNING id, name, email, agency_slug, agency_status
    `, [status, userId]);

    const agency = updated.rows[0];
    if (!agency) {
      return { success: false, error: 'Agency not found' };
    }

    // A rejected agency is not public: what it had online goes back through
    // moderation (the same state an agency that is not approved gets for its
    // edits), instead of staying live under an agency the site has refused.
    if (status === 'rejected') {
      await query(
        `UPDATE properties SET status = 'pending', approved_at = NULL, updated_at = NOW()
         WHERE user_id = $1 AND status = 'active'`,
        [userId]
      );
    }

    // The agency should not have to poll the dashboard to learn the outcome.
    if (status === 'approved' || status === 'rejected') {
      try {
        const base = await getSiteUrl();
        const cabinetUrl = `${base}/en/cabinet/realtor/settings`;
        if (status === 'approved') {
          await sendAgencyApprovedEmail({
            to: agency.email,
            agencyName: agency.name || 'Your agency',
            siteUrl: `${base}${agencyPath('en', agency)}`,
            cabinetUrl,
          });
        } else {
          await sendAgencyRejectedEmail({
            to: agency.email,
            agencyName: agency.name || 'Your agency',
            cabinetUrl,
          });
        }
      } catch (mailError) {
        console.error('Agency status email failed (status was saved):', mailError);
      }
    }

    await createAuditLog(
      status === 'approved' ? 'agency_approved' : status === 'rejected' ? 'agency_rejected' : 'agency_status_changed',
      'user',
      userId,
      { status },
      session.email
    );

    revalidatePath('/admin/users');
    return { success: true };
  } catch (e: any) {
    console.error('Error updating agency status:', e);
    return { success: false, error: publicError(e) };
  }
}

